/**
 * «Помощник покупок» (виджет cdp-web-chat, ТЗ §7.6) — панель-лаунчер на реальной
 * витрине, работающая с ТОЙ ЖЕ корзиной, что и сама витрина (общий Redis-ключ
 * `storefront:cart:<tenant>:<profileId>` — см. cart-service.ts и engine
 * bot-agent-invoker.ts).
 *
 * Тот же self-disabling паттерн, что и `stores/cdp.ts`: BFF (`/api/v1/chat/init-token`)
 * отдаёт `writeKey` только если инструмент-чат настроен на этом деплое
 * (`CDP_CHAT_WRITE_KEY`) — без него фича молча выключена, витрина не задета.
 * `chatInitToken` отдельно может быть null (PAT не настроен/движок недоступен/
 * анонимная сессия не завелась) — в этом случае виджет ВСЁ РАВНО монтируется
 * (плавающая кнопка), просто SDK инициализируется анонимно (штатный fallback).
 *
 * Виджет — launcher (плавающая кнопка), НЕ инлайн: НЕ вызываем widget.open()
 * при монтировании, чтобы не перекрывать витрину сразу — виджет сам помнит,
 * была ли панель открыта (persistVisibility, ключ в localStorage).
 */
import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
import { storefront } from "../api/storefront";
import { brand } from "../brand";
import { loadChatSdk, type ChatSdkModule, type ChatWidgetLike } from "../cdp/chat-sdk-loader";
import { alignLauncher } from "../cdp/launcher-align";
import { useCartStore } from "./cart";

const MOUNT_ELEMENT_ID = "cdp-chat-widget-root";

/** Список чатов выключен: у помощника один разговор. */
export const ASSISTANT_THREAD_LIST = {
  enabled: false,
  threads: [],
  muted_badge_style: "hidden",
  search_visible: false,
  unsubscribe_from_thread: false,
  unsubscribe_from_list: false,
  notifications_thread_visible: false,
} as const;

export const useChatStore = defineStore("chat", () => {
  const enabled = ref(false);
  const initialized = ref(false);
  const widget = shallowRef<ChatWidgetLike | null>(null);
  const client = shallowRef<ReturnType<ChatSdkModule["createChatClient"]> | null>(null);
  let generation = 0;
  let initPromise: Promise<void> | null = null;

  function init(): Promise<void> {
    if (initPromise) return initPromise;
    if (initialized.value) return Promise.resolve();
    const running = initialize();
    initPromise = running;
    return running.finally(() => { if (initPromise === running) initPromise = null; });
  }

  async function initialize(): Promise<void> {
    const ownGeneration = ++generation;

    let ctx: Awaited<ReturnType<typeof storefront.chat.initToken>>;
    try {
      ctx = await storefront.chat.initToken();
    } catch {
      // Инфра-сбой BFF/сети — следующий явный вызов init() может повторить попытку.
      return;
    }
    if (ownGeneration !== generation) return;
    if (!ctx?.writeKey) { initialized.value = true; return; } // инструмент-чат не настроен на этом деплое
    // Общая корзина гарантирована только подписанным токеном профиля. Анонимный SDK fallback
    // создаёт отдельную корзину, поэтому при временном сбое минтинга оставляем помощник выключенным
    // и разрешаем следующему явному init() повторить попытку.
    if (!ctx.chatInitToken) return;

    let sdk: ChatSdkModule;
    try {
      sdk = await loadChatSdk();
    } catch {
      // Бандл не задеплоен/не отдан по ожидаемому пути — деградируем молча.
      return;
    }

    if (ownGeneration !== generation) return;
    let container = document.getElementById(MOUNT_ELEMENT_ID);
    if (!container) {
      container = document.createElement("div");
      container.id = MOUNT_ELEMENT_ID;
      document.body.appendChild(container);
    }

    const nextClient = sdk.createChatClient({
      baseUrl: window.location.origin,
      writeKey: ctx.writeKey,
      ...(ctx.chatInitToken ? { chatInitToken: ctx.chatInitToken } : {}),
      // Без experienceId движок отдаёт SDK-дефолтную тему, а не брендовую ветку Experience (живой препрод 24.09).
      ...(ctx.experienceId ? { experienceId: ctx.experienceId } : {}),
      storageNamespace: `storefront-${brand.id}`,
      // Предпочтение хоста по классу html.dark витрины. Фиксированная тема и запрет тёмной темы
      // в настройках Experience по-прежнему решаются SDK и имеют приоритет.
      theme: document.documentElement.classList.contains("dark") ? "dark" : "light",
    });

    widget.value = sdk.createChatWidget({
      container,
      client: nextClient,
      mode: "launcher",
      showLauncher: false,
      shadowDom: true,
      persistVisibility: true,
      // Окно по центру почти на весь экран, сайт приглушён (Игорь 01.10).
      modal: true,
      // Кнопка «Мои наборы» в шапке помощника: список, переименование, удаление (Игорь 01.10 п.10).
      mySets: true,
      // Кнопка-щит «Мои ограничения» (дорожка Д): аллергии, «не ем», халяль, без алкоголя — из словаря магазина.
      constraints: true,
      capabilities: true,
      // Кнопка «Рецепты»: книга рецептов магазина — фильтр по продуктам, времени, диете, «из корзины» (Игорь 01.10 В10).
      collectionBrowser: { label: "Рецепты", icon: "book" },
      // Телефон: подбор каруселью и мини-карточки прямо в разговоре, подробности листом (Игорь 01.10 п.12).
      phoneInline: true,
      // Голос: у записи переключатель «Голосовым / Текстом в поле» (распознавание на сервере, Игорь 01.10 п.15).
      voiceDictation: true,
      title: `Помощник ${brand.shortName}`,
      // Персона магазина задаётся штатным слоем текстов SDK, не меняет другие каналы.
      texts: { chat: {
        assistant: "Помощница",
        typing: (name: string) => name === "Помощница" ? "Помощница думает" : `${name} печатает`,
        typingEllipsis: (name: string) => name === "Помощница" ? "Помощница думает…" : `${name} печатает…`,
      } },
      // Помощник открывается сразу в разговоре: без экрана «Чаты», уведомлений и «Чата с поддержкой»
      // (Игорь 01.10). Переопределяет thread_list из настроек Experience, где он включён по умолчанию.
      presentationConfig: { thread_list: { ...ASSISTANT_THREAD_LIST } },
      // Корзина общая с сайтом: помощник собрал/отменил — шапка и нижнее меню перечитывают корзину (приёмка 02.10:
      // панель «3 товара · 307 ₽», а шапка оставалась «0 ₽»).
      onCartChange: () => { if (ownGeneration === generation) void useCartStore().fetch().catch(() => undefined); },
    });

    if (ownGeneration !== generation) { widget.value?.destroy?.(); nextClient.destroy?.(); return; }
    client.value = nextClient;
    enabled.value = true;
    initialized.value = true;
    // Кнопка помощника — по центру нижней строки «В корзину» (переменная наследуется в shadow DOM виджета).
    if (typeof window !== "undefined" && typeof MutationObserver !== "undefined") alignLauncher(container);

    // Best-effort: если инициализация SDK не удастся (истёкший токен, сеть) — виджет
    // остаётся смонтированным (кнопка видна), сам SDK покажет собственное состояние
    // ошибки внутри панели при открытии. Витрину это не роняет в любом случае.
    try {
      const initOptions = {
        ...(ctx.chatInitToken ? { chatInitToken: ctx.chatInitToken } : {}),
        ...(ctx.experienceId ? { experienceId: ctx.experienceId } : {}),
      };
      await nextClient.init(Object.keys(initOptions).length ? initOptions : undefined);
    } catch {
      /* см. комментарий выше */
    }
  }

  function reset(): void {
    generation++;
    initPromise = null;
    widget.value?.destroy?.();
    client.value?.destroy?.();
    widget.value = null;
    client.value = null;
    enabled.value = false;
    initialized.value = false;
    document.getElementById(MOUNT_ELEMENT_ID)?.remove();
  }

  async function reload(): Promise<void> { reset(); await init(); }

  function setTheme(theme: "light" | "dark"): void { widget.value?.setTheme?.(theme); }

  function open(): boolean {
    if (!enabled.value || !widget.value) return false;
    try { widget.value.open(); return true; }
    catch { return false; }
  }

  async function openRecipe(recipeId: string): Promise<boolean> {
    await init();
    if (!enabled.value || !widget.value?.openCollection) return false;
    try { return await widget.value.openCollection(recipeId); }
    catch { return false; }
  }

  /** Отправить сформированный пользователем запрос в текущий разговор помощника. */
  async function sendPrompt(text: string): Promise<boolean> {
    await init();
    if (!enabled.value || !client.value || !text.trim()) return false;
    try {
      widget.value?.open();
      await client.value.sendMessage(text.trim());
      return true;
    } catch {
      return false;
    }
  }

  return { enabled, initialized, init, reload, reset, setTheme, open, openRecipe, sendPrompt };
});
